export const getStepSize = (variant) => {
    if (!!!variant.storeUnit)
        return 1;
    const { isWeight, minimumWeight, multiple } = variant.storeUnit;
    if (isWeight && minimumWeight)
        return minimumWeight;
    if (multiple > 0)
        return multiple;
    return 1;
};
export const getNumberWithinLimits = (value, min, max) => {
    if (min && value < min)
        return min;
    if (max && value > max)
        return max;
    return value;
};
export const floatCalculationSafety = (safetyExponential = 3) => {
    const pow = Math.pow(10, safetyExponential);
    return {
        preFormat: (n) => n * pow,
        postFormat: (n) => n / pow,
    };
};
export const adjustNumberToMatchStep = (value, step, packageWeight) => {
    const { preFormat, postFormat } = floatCalculationSafety();
    const stepSize = preFormat(step);
    const desiredValue = preFormat(value);
    if (step === 1 && !!packageWeight && !Number.isInteger(packageWeight)) {
        return adjustKgdDecimalPackageWeight(value, step, packageWeight);
    }
    return postFormat(stepSize * Math.ceil(desiredValue / stepSize));
};
const adjustKgdDecimalPackageWeight = (value, step, packageWeight) => {
    const { preFormat, postFormat } = floatCalculationSafety();
    // Scaled representations to avoid floating point issues
    const scaledValue = preFormat(value);
    const scaledPackage = preFormat(packageWeight);
    const scaledStep = preFormat(step);
    const scaledOne = preFormat(1);
    // Helpers ---------------------------------------------------------------
    // Split a scaled number into whole and fractional parts relative to scaledOne
    const splitScaledParts = (n) => ({
        whole: Math.floor(n / scaledOne),
        fraction: n % scaledOne,
    });
    // True if scaled n is an exact multiple of scaled m
    const isScaledMultiple = (n, m) => n % m === 0;
    // Round a scaled number up to the nearest multiple of scaledStep
    const roundUpToScaledStep = (n, stepScaled) => stepScaled * Math.ceil(n / stepScaled);
    // End helpers -----------------------------------------------------------
    // Fast-path: already aligned with package or whole-unit rules -> return original value
    if (scaledValue === scaledPackage ||
        isScaledMultiple(scaledValue, scaledPackage) ||
        (scaledValue < scaledPackage && isScaledMultiple(scaledValue, scaledOne))) {
        return value;
    }
    const valueParts = splitScaledParts(scaledValue);
    const packageParts = splitScaledParts(scaledPackage);
    // If value is less than a package and its whole part is strictly less than package whole,
    // round up to the nearest step increment.
    if (scaledValue < scaledPackage && valueParts.whole < packageParts.whole) {
        const scaledResult = roundUpToScaledStep(scaledValue, scaledStep);
        return postFormat(scaledResult);
    }
    // If value shares the same whole part as package but has a smaller fractional part,
    // it should be bumped up to one package.
    if (valueParts.whole === packageParts.whole &&
        valueParts.fraction < packageParts.fraction) {
        return packageWeight;
    }
    // Consider the next package multiple
    const nextPackageCount = Math.ceil(scaledValue / scaledPackage);
    const scaledNextPackageTotal = nextPackageCount * scaledPackage;
    const nextPackageParts = splitScaledParts(scaledNextPackageTotal);
    // If the next package total keeps the same whole part but increases the fraction,
    // use that next package total.
    if (nextPackageParts.whole === valueParts.whole &&
        valueParts.fraction < nextPackageParts.fraction) {
        return postFormat(scaledNextPackageTotal);
    }
    // Compute how many full packages fit and the scaled remainder
    const fullPackageCount = Math.floor(scaledValue / scaledPackage);
    const scaledFullPackagesWeight = fullPackageCount * scaledPackage;
    const scaledRemainder = scaledValue - scaledFullPackagesWeight;
    const previousPackageParts = splitScaledParts(scaledFullPackagesWeight);
    // If current quantity is in the previous whole unit with a fractional part,
    // move directly to the next package total.
    const isQuantityInPreviousWholeUnit = nextPackageParts.whole - step === valueParts.whole;
    const hasCrossedPackageFractionThreshold = valueParts.fraction > previousPackageParts.fraction;
    if (isQuantityInPreviousWholeUnit &&
        hasCrossedPackageFractionThreshold &&
        scaledRemainder > 0) {
        return postFormat(scaledNextPackageTotal);
    }
    // If there is at least one full package and a non-zero remainder,
    // round the remainder up to the nearest step and add to the full packages.
    if (fullPackageCount >= 1 && !isScaledMultiple(scaledValue, scaledPackage)) {
        const roundedRemainderScaled = roundUpToScaledStep(scaledRemainder, scaledStep);
        const finalScaled = scaledFullPackagesWeight + roundedRemainderScaled;
        return postFormat(finalScaled);
    }
    // Fallback: round up to the nearest step increment
    const fallbackScaled = roundUpToScaledStep(scaledValue, scaledStep);
    return postFormat(fallbackScaled);
};
